import { action, runInAction, makeObservable } from 'mobx';
import { eventBus, subscribe } from 'mobx-event-bus2';
import { AxiosError } from 'axios';

import { reverse } from 'named-urls';

import Logger from 'utils/logger';

import API from 'utils/api';
import { EventType } from 'utils/events/constants';
import { AnswerTypes } from 'utils/api/modules/incidents/incidents.types';
import { EntityValueType } from 'utils/api/modules/entities/entities.types';
import { isBadRequest, isNotFound } from 'utils/api/errors';

import { EntityModules } from 'core/constants';

import routes from 'core/routes';
import { risksRegisterPath } from 'utils/risksRegisterPath';

import EntitiesActivities from 'stores/EntitiesActivities';

import RootStore from '../Root';
import EntityDetails from '../EntityDetails/EntityDetails';

export default class RisksDetailsStore extends EntityDetails {
  constructor(rootStore: RootStore, api: typeof API) {
    super(rootStore, api, EntityModules.Risks, routes.dashboard.risks.toString(), (uuid: string): string =>
      reverse(routes.dashboard.risks.details, { entityId: uuid })
    );

    makeObservable(this);

    eventBus.register(this);
  }

  @action.bound
  async loadDetails(riskId: string): Promise<void> {
    this.isLoaded = false;
    this.path = risksRegisterPath(this.store.session.user);
    try {
      const { data } = await this.api.entities.loadEntitiesDetails(this.entityType, riskId)();
      runInAction(() => {
        this.activities = new EntitiesActivities(this.store, this.api, riskId, this.entityType);
        this.error = undefined;
        this.details = data;
      });
    } catch (e) {
      runInAction(() => {
        // @ts-ignore
        this.error = e;
      });
      // @ts-ignore
      if (!isNotFound(e)) {
        Logger.error(`Invalid load risk details API response. Risk id: ${riskId}`, e);
      }
    } finally {
      runInAction(() => {
        this.isLoaded = true;
      });
    }
  }

  create = async (riskName: string): Promise<boolean | string> => {
    try {
      const {
        data: { uuid },
      } = await this.api.entities.createEntity(this.entityType, { [`${this.prefix}name`]: riskName })();
      return uuid;
    } catch (e) {
      Logger.error('Failed to create risk', e);

      return false;
    }
  };

  delete = async (riskId: string): Promise<boolean> => {
    try {
      await this.api.entities.deleteEntity(this.entityType, riskId)();
    } catch (e) {
      Logger.error('Failed to delete risk', e);
      return false;
    }
    return true;
  };

  edit = async (data: Record<string, EntityValueType>): Promise<boolean> => {
    if (this.details?.uuid === undefined) {
      return false;
    }
    try {
      const edited = await this.api.entities.editEntity(this.entityType, this.details?.uuid, data)();
      this.details = edited.data;
      this.isContentEdited = false;
      this.store.notification.enqueueSuccessSnackbar('Risk edited successfully');
      return true;
    } catch (e) {
      const error = e as AxiosError<string[]>;
      this.store.notification.enqueueErrorSnackbar(
        error.response?.data?.[0] || `The risk could not be updated. Something went wrong.`
      );
      Logger.error('Failed to edit risk', e);
      return false;
    }
  };

  addControl = async (riskUuid: string, controlUuid: string): Promise<{ added: boolean; refusal?: string }> => {
    try {
      await this.api.entities.addRiskControl(riskUuid, controlUuid)();
      return { added: true };
    } catch (e) {
      const error = e as AxiosError<{ control?: string[] }>;
      const refusal = isBadRequest(error) ? error.response?.data?.control?.[0] : undefined;
      if (!refusal) {
        this.store.notification.enqueueErrorSnackbar("Couldn't add the control. Try again.");
      }
      return { added: false, refusal };
    }
  };

  removeControl = async (riskUuid: string, controlUuid: string): Promise<boolean> => {
    try {
      await this.api.entities.removeRiskControl(riskUuid, controlUuid)();
      this.store.notification.enqueueSuccessSnackbar('Control removed from this risk');
    } catch (e) {
      if (!isNotFound(e as AxiosError)) {
        this.store.notification.enqueueErrorSnackbar("Couldn't remove the control. Try again.");
        return false;
      }
    }
    return true;
  };

  @subscribe(EventType.LoggedOut)
  @action
  reset(): void {
    this.isLoaded = false;
    this.error = undefined;
    this.details = undefined;
  }

  @action.bound
  contentHasBeenEdited(questionId: string, newValue: AnswerTypes): void {
    const question = this.details?.entity[questionId];
    if (question?.value !== newValue) {
      this.isContentEdited = true;
    } else {
      this.isContentEdited = false;
    }
  }
}
